import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, Resource as R } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { ContextualMenu } from "@common/contextualMenu";
import { Filter } from "@common/filter";
import { useModal } from "@root/client/modal";
import { Resource } from "../resource";
import { applySorters } from "../sort";
import { SkeletonDisplay } from "./skeletonDisplay";
import { ResourceSelect } from "./resourceSelect";
import { Placeholder } from "./placeholder";
import { Props } from "./types";
import styles from "./styles.scss";
import classNames from "classnames";
import { CogWheelIcon } from "@common/cogWheelIcon";

function GroupsList(props: Props) {
  const { t, i18n } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";
  const isFiltered = (resource: Identifiable<R>) =>
    (props.filter.resourceTypes.length === 0 ||
      props.filter.resourceTypes.includes(resource.type)) &&
    (props.filter.roles.length === 0 ||
      props.filter.roles.find(
        ({ uuid }) => resource.role && resource.role.uuid === uuid,
      )) &&
    (!props.filter.inactiveOnly || resource.deactivated) &&
    (!props.filter.activeOnly || !resource.deactivated);

  return props.isRequestPending ? (
    <div className={styles.groupsList}>
      <SkeletonDisplay />
      <SkeletonDisplay />
    </div>
  ) : props.groups.length === 0 ? (
    <Placeholder />
  ) : (
    <div className={styles.groupsList}>
      {[...props.groups]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((group) => (
          <div key={group.uuid} className={styles.group}>
            <div className={styles.resourcesList}>
              <div className={styles.heading}>
                <p className={styles.label}>
                  {t("planzoneMembersPage.resources.name")}
                </p>
                {props.columns.map(({ key }) => {
                  const criteria = template.find(({ uuid }) => uuid === key);
                  const label = criteria
                    ? criteria.labels[
                        currentLang as keyof typeof criteria.labels
                      ] || criteria.name
                    : t(`planzoneMembersPage.resources.${key}`, key);

                  return (
                    <p key={key} className={styles.label}>
                      {label}
                    </p>
                  );
                })}
                <Safeguard
                  useCases={[UseCases.editResourcesListingConfiguration]}
                >
                  {({ canEditResourcesListingConfiguration }) => (
                    <div className={classNames(styles.label, styles.cogWheel)}>
                      {canEditResourcesListingConfiguration && (
                        <div
                          onClick={() =>
                            pushView({
                              key: UseCases.retrieveResourcesListingConfiguration,
                              data: undefined,
                            })
                          }
                        >
                          <CogWheelIcon className={styles.cogIcon} />
                        </div>
                      )}
                    </div>
                  )}
                </Safeguard>
              </div>
              <Filter
                data={group.resources}
                filter={props.filter}
                extractCriteria={(resource) => ({ resource })}
              >
                {(filteredResources) => (
                  <ul className={styles.list}>
                    {applySorters(filteredResources, props.sort)
                      .filter(isFiltered)
                      .map((resource) => (
                        <li key={resource.uuid} className={styles.item}>
                          <Resource
                            resource={resource}
                            columns={props.columns}
                          />
                        </li>
                      ))}
                  </ul>
                )}
              </Filter>
            </div>
            <div className={styles.groupHeader}>
              <div>
                <h2 className={styles.name}>{group.name}</h2>
                <p className={styles.members}>
                  {group.resources.length} {t("membersList.groupLength")}
                </p>
              </div>
              <Safeguard
                useCases={[
                  UseCases.editResourceGroup,
                  UseCases.removeResourceGroup,
                ]}
              >
                {({ canEditResourceGroup, canRemoveResourceGroup }) =>
                  (canEditResourceGroup || canRemoveResourceGroup) && (
                    <div className={styles.actions}>
                      <ResourceSelect
                        resources={group.resources as Identifiable<R>[]}
                        group={group}
                      />
                      <div className={styles.menu}>
                        <ContextualMenu
                          items={[
                            ...(canEditResourceGroup
                              ? [
                                  {
                                    name: t("membersList.contextualMenu.edit"),
                                    act: () =>
                                      pushView({
                                        key: UseCases.editResourceGroup,
                                        data: {
                                          group,
                                          namespace: planzone.uuid,
                                        },
                                      }),
                                  },
                                ]
                              : []),
                            ...(canRemoveResourceGroup
                              ? [
                                  {
                                    name: t(
                                      "membersList.contextualMenu.delete",
                                    ),
                                    color: "#DB5F63FF",
                                    act: () =>
                                      pushView({
                                        key: UseCases.removeResourceGroup,
                                        data: group,
                                      }),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </div>
                    </div>
                  )
                }
              </Safeguard>
            </div>
          </div>
        ))}
    </div>
  );
}

export { GroupsList };
